import React, { forwardRef } from "react";
import { Box } from "@mui/material";

import { TSCTheme } from "@/ui/theme/commonTheme";

export const VARIANT = {
  OUTLINED: "outlined",
  ROUNDED: "rounded",
  SHARP: "sharp",
} as const;

export type Variant = (typeof VARIANT)[keyof typeof VARIANT];

export const SYMBOL_WEIGHTS = {
  XTHIN: 100,
  THIN: 200,
  LIGHT: 300,
  REGULAR: 400,
  BOLD: 500,
  XBOLD: 600,
  XXBOLD: 700,
} as const;

export type SymbolWeight = (typeof SYMBOL_WEIGHTS)[keyof typeof SYMBOL_WEIGHTS];

const SYMBOL_GRADES = {
  LOW: -25,
  REGULAR: 0,
  HIGH: 25,
} as const;

export const SYMBOL_OPTICAL_SIZES = {
  XSMALL: 20,
  SMALL: 24,
  LARGE: 40,
  XLARGE: 48,
} as const;

export const SYMBOL_SIZES = {
  INHERIT: "inherit",
  XXSMALL: "16px",
  XSMALL: "20px",
  SMALL: "24px",
  MEDIUM: "32px",
  LARGE: "40px",
  XLARGE: "48px",
} as const;

export type SymbolSize = (typeof SYMBOL_SIZES)[keyof typeof SYMBOL_SIZES];

// 1. Extract all deeply nested paths from the theme palette object
export type Paths<T> = T extends object
  ? {
      [K in keyof T]: K extends string | number
        ? T[K] extends object
          ? `${K}` | `${K}.${Paths<T[K]>}`
          : `${K}`
        : never;
    }[keyof T]
  : never;

// 2. Restrict the string input strictly to valid MUI palette paths
export type PaletteColorPath = Paths<TSCTheme["palette"]>;

/**
 * Safely resolves a dot-notation path from the MUI theme palette.
 */
export const getThemeColor = (
  theme: TSCTheme,
  path: PaletteColorPath
): string | undefined => {
  // Walk the palette one key at a time; a path that ends anywhere but a string (not one
  // PaletteColorPath allows) resolves to undefined (as before P7), never an object
  let node: unknown = theme.palette;
  for (const part of path.split(".")) {
    node =
      typeof node === "object" && node !== null
        ? Reflect.get(node, part)
        : undefined;
  }
  return typeof node === "string" ? node : undefined;
};

export type MaterialSymbolProps = {
  symbol: string;
  weight?: SymbolWeight;
  size?: SymbolSize | (string & {}) | number;
  variant?: Variant;
  fill?: boolean;
  color?: PaletteColorPath | "inherit";
  opticalSize?: number;
  darkMode?: boolean;
  className?: string;
  sx?: object;
} & React.HTMLAttributes<HTMLSpanElement>;

export const MaterialSymbol = forwardRef<HTMLSpanElement, MaterialSymbolProps>(
  (
    {
      symbol,
      weight = SYMBOL_WEIGHTS.LIGHT,
      size = SYMBOL_SIZES.INHERIT,
      variant = VARIANT.OUTLINED,
      fill = false,
      color = "inherit",
      opticalSize,
      darkMode,
      className,
      sx,
      ...otherProps
    },
    ref
  ) => {
    const grade = darkMode ? SYMBOL_GRADES.LOW : SYMBOL_GRADES.REGULAR;

    if (!opticalSize) {
      switch (size) {
        case SYMBOL_SIZES.XSMALL:
          opticalSize = SYMBOL_OPTICAL_SIZES.XSMALL;
          break;
        case SYMBOL_SIZES.SMALL:
        case SYMBOL_SIZES.MEDIUM:
          opticalSize = SYMBOL_OPTICAL_SIZES.SMALL;
          break;
        case SYMBOL_SIZES.LARGE:
          opticalSize = SYMBOL_OPTICAL_SIZES.LARGE;
          break;
        case SYMBOL_SIZES.XLARGE:
          opticalSize = SYMBOL_OPTICAL_SIZES.XLARGE;
          break;
        default:
          opticalSize = SYMBOL_OPTICAL_SIZES.SMALL;
          break;
      }
    }

    return (
      <Box
        {...otherProps}
        ref={ref}
        component="span"
        className={
          className
            ? `material-symbols-${variant} notranslate ${className}`
            : `material-symbols-${variant} notranslate`
        }
        sx={{
          fontSize: size,
          color:
            color === "inherit" ? null : (theme) => getThemeColor(theme, color),
          fontVariationSettings: `'FILL' ${
            fill ? "1" : "0"
          }, 'wght' ${weight}, 'GRAD' ${grade}, 'opsz' ${opticalSize};`,
          ...sx,
        }}
      >
        {symbol}
      </Box>
    );
  }
);
